import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, doc, getDoc, updateDoc } from 'firebase/firestore';

// --- UTILS: Platform & Sound Management ---
const isAndroidNative = () => /NexoraAndroidApp/i.test(navigator.userAgent) || !!window.Android;

const stopAllRingtones = () => {
  try {
    if (window.nexoraIncomingAudio) {
      window.nexoraIncomingAudio.pause();
      window.nexoraIncomingAudio.currentTime = 0;
    }
  } catch (e) {}
  
  // ব্রাউজারের সকল অডিও ট্যাগ পজ করা
  document.querySelectorAll('audio').forEach(a => {
    try { a.pause(); a.currentTime = 0; } catch (e) {}
  });
};

const playRingingTone = () => {
  if (isAndroidNative()) return; // অ্যান্ড্রয়েড অ্যাপ নিজে রিংটোন বাজাবে, তাই ওয়েবে বন্ধ থাকবে।
  
  stopAllRingtones();
  try {
    if (!window.nexoraIncomingAudio) {
      window.nexoraIncomingAudio = new Audio('/Ringing.mp3');
      window.nexoraIncomingAudio.loop = true;
    }
    window.nexoraIncomingAudio.volume = 1.0;
    const playPromise = window.nexoraIncomingAudio.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        console.log("Autoplay blocked: User interaction required for sound.");
      });
    }
  } catch (e) {}
};

const closeIncomingCallNotifications = async () => {
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations?.();
    if (!registrations) return;

    for (const registration of registrations) {
      const notifications = await registration.getNotifications?.({ tag: 'nexora-call-notification' });
      if (!notifications) continue;
      notifications.forEach(notification => notification.close());
    }
  } catch (e) {
    console.warn('Could not close incoming call notification:', e);
  }
};

const IncomingCallListener = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [incomingCall, setIncomingCall] = useState(null);
  const [callerData, setCallerData] = useState(null);
  
  const activeCallDocIdRef = useRef(null);
  const currentUid = auth.currentUser?.uid || localStorage.getItem('nexora_user_uid');

  useEffect(() => {
    if (!currentUid || location.pathname === '/call') {
      stopAllRingtones();
      return;
    }

    // Firestore Listener for ringing calls
    const q = query(
      collection(db, 'calls'),
      where('receiverUid', '==', currentUid),
      where('status', '==', 'ringing')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      if (!snapshot.empty) {
        const callDoc = snapshot.docs[0];
        const callData = { id: callDoc.id, ...callDoc.data() };
        const createdAtValue = callData.createdAt && typeof callData.createdAt.toMillis === 'function'
          ? callData.createdAt.toMillis()
          : Number(callData.createdAt || 0);

        if (createdAtValue && Date.now() - createdAtValue > 60000) {
          try {
            await updateDoc(doc(db, 'calls', callData.id), {
              status: 'missed',
              endedAt: new Date()
            });
          } catch (e) {}

          activeCallDocIdRef.current = null;
          setIncomingCall(null);
          setCallerData(null);
          stopAllRingtones();
          closeIncomingCallNotifications();
          return;
        }

        // যদি অলরেডি এই কলটি হ্যান্ডেল না হয়ে থাকে
        if (activeCallDocIdRef.current !== callData.id) {
          activeCallDocIdRef.current = callData.id;
          setIncomingCall(callData);
          playRingingTone();

          // Fetch Caller Info
          try {
            const userSnap = await getDoc(doc(db, 'users', callData.callerUid));
            if (userSnap.exists()) setCallerData(userSnap.data());
          } catch (e) {}
        }
      } else {
        // কল কেটে দিলে বা রিসিভ হয়ে গেলে পপআপ রিমুভ ও সাউন্ড বন্ধ
        activeCallDocIdRef.current = null;
        setIncomingCall(null);
        stopAllRingtones();
        closeIncomingCallNotifications();
      }
    });

    return () => {
      unsubscribe();
      stopAllRingtones();
    };
  }, [currentUid, location.pathname]);

  const handleAccept = async () => {
    if (!incomingCall) return;
    
    stopAllRingtones();
    try {
      await updateDoc(doc(db, 'calls', incomingCall.id), { status: 'accepted' });
      closeIncomingCallNotifications();
      
      navigate('/call', {
        state: {
          targetUid: incomingCall.callerUid,
          callType: incomingCall.callType,
          callId: incomingCall.sharedCallId || incomingCall.id,
          isIncoming: true,
          isGroupCall: incomingCall.isGroupCall || false,
          groupId: incomingCall.groupId || null,
          groupName: incomingCall.groupName || null
        }
      });
    } catch (e) {
      console.error("Accept error:", e);
    }
    setIncomingCall(null);
  };

  const handleDecline = async () => {
    if (!incomingCall) return;
    
    stopAllRingtones();
    try {
      await updateDoc(doc(db, 'calls', incomingCall.id), { 
        status: 'rejected',
        endedAt: new Date()
      });
    } catch (e) {}
    closeIncomingCallNotifications();
    setIncomingCall(null);
    activeCallDocIdRef.current = null;
  };

  if (!incomingCall) return null;

  return (
    <div className="incoming-overlay">
      <style>{`
        .incoming-overlay {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10000;
          width: 90%;
          max-width: 400px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(15px);
          border-radius: 24px;
          padding: 15px 20px;
          box-shadow: 0 15px 35px rgba(0,0,0,0.2);
          border: 1px solid #22c55e;
          display: flex;
          align-items: center;
          justify-content: space-between;
          animation: slideDown 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        @keyframes slideDown {
          from { transform: translate(-50%, -100px); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }

        .caller-info { display: flex; align-items: center; gap: 12px; }
        .caller-avatar { width: 50px; height: 50px; border-radius: 50%; object-fit: cover; border: 2px solid #22c55e; }
        .action-btns { display: flex; gap: 12px; }
        
        .btn-call { width: 45px; height: 45px; border-radius: 50%; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; color: white; font-size: 20px; }
        .decline { background: #ef4444; transform: rotate(135deg); }
        .accept { background: #22c55e; box-shadow: 0 5px 15px rgba(34, 197, 94, 0.4); }
      `}</style>

      <div className="caller-info">
        <img 
          src={callerData?.photoURL || "https://cdn-icons-png.flaticon.com/512/149/149071.png"} 
          className="caller-avatar" 
          alt="caller" 
        />
        <div>
          <h4 style={{ margin: 0, color: '#0f172a', fontSize: '15px', fontWeight: '800' }}>
            {incomingCall.isGroupCall ? (incomingCall.groupName || "Group Call") : (callerData?.fullName || "Nexora User")}
          </h4>
          <p style={{ margin: 0, color: '#62b875', fontSize: '12px', fontWeight: '600' }}>
            Incoming {incomingCall.callType} call...
          </p>
        </div>
      </div>

      <div className="action-btns">
        <button onClick={handleDecline} className="btn-call decline">📞</button>
        <button onClick={handleAccept} className="btn-call accept">📞</button>
      </div>
    </div>
  );
};

export default IncomingCallListener;